import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const HeroCarousel = ({ slides, interval = 6000, showSlideText = false }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const goToNext = useCallback(() => {
    if (isTransitioning || slides.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 1000);
  }, [isTransitioning, slides.length]);

  const goToPrev = useCallback(() => {
    if (isTransitioning || slides.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 1000);
  }, [isTransitioning, slides.length]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 1000);
  }, [isTransitioning, currentIndex]);

  // Auto-advance slides
  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    
    const timer = setInterval(goToNext, interval);
    return () => clearInterval(timer);
  }, [goToNext, interval, isPaused, slides.length]);

  if (!slides || slides.length === 0) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/40" />
    );
  }

  return (
    <div 
      className="absolute inset-0 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={cn(
            "absolute inset-0 transition-all duration-1000 ease-in-out",
            index === currentIndex 
              ? "opacity-100 scale-100" 
              : "opacity-0 scale-105"
          )}
        >
          {/* Background Image */}
          <img
            src={slide.image_url}
            alt={slide.title?.[lang] || slide.title?.fr || "Hero"}
            className="w-full h-full object-cover"
            loading={index === 0 ? "eager" : "lazy"}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />
          
          {/* Text Overlay with animation - Only show if showSlideText is true */}
          {showSlideText && (slide.title || slide.subtitle) && (
            <div 
              className={cn(
                "absolute inset-0 flex items-center justify-center",
                "transition-all duration-700 delay-300",
                index === currentIndex 
                  ? "opacity-100 translate-y-0" 
                  : "opacity-0 translate-y-4"
              )}
            >
              <div className="text-center px-4 max-w-4xl">
                {slide.title && (
                  <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
                    {typeof slide.title === "object" 
                      ? (slide.title[lang] || slide.title.fr || slide.title.en || "") 
                      : slide.title}
                  </h2>
                )}
                {slide.subtitle && (
                  <p className="text-lg md:text-xl lg:text-2xl text-white/90 drop-shadow-md">
                    {typeof slide.subtitle === "object" 
                      ? (slide.subtitle[lang] || slide.subtitle.fr || slide.subtitle.en || "") 
                      : slide.subtitle}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Navigation Arrows - Always visible on hover */}
      {slides.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 
                       p-2 md:p-3 rounded-full bg-white/10 backdrop-blur-sm 
                       hover:bg-white/20 transition-all duration-300
                       text-white hover:scale-110 active:scale-95"
            aria-label="Previous slide"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 
                       p-2 md:p-3 rounded-full bg-white/10 backdrop-blur-sm 
                       hover:bg-white/20 transition-all duration-300
                       text-white hover:scale-110 active:scale-95"
            aria-label="Next slide"
            data-testid="carousel-next"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {slides.length > 1 && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                "w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300",
                index === currentIndex 
                  ? "bg-white w-6 md:w-8" 
                  : "bg-white/50 hover:bg-white/70"
              )}
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}

      {/* Progress Bar */}
      {slides.length > 1 && !isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-20">
          <div 
            key={currentIndex}
            className="h-full bg-primary"
            style={{
              animation: `progress ${interval}ms linear forwards`,
            }}
          />
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default HeroCarousel;
